/*
 * Copyright (c) 2022 GM Global Technology Operations LLC. All rights reserved.
 * This information is confidential and proprietary to GM Global Technology
 * Operations LLC and may not be used, modified, copied or distributed.
 */

import "lib/mods"
import { PublicClientApplication } from "@azure/msal-browser"
import { MsalProvider } from "@azure/msal-react"
import { CssBaseline } from "@mui/material"
import { AppWithAuth } from "App"
import { Loading } from "components"
import { MsalConfig } from "config"
import { ThemeProvider } from "lib/hooks"
import React, { useEffect, useState } from "react"
import { createRoot } from "react-dom/client"
import { Provider } from "react-redux"
import { Store, UserSlice } from "store"

import ReportWebVitals from "./reportWebVitals"
import * as util from "lib/util"

const container = document.getElementById("root")
const root = createRoot(container!)

const msalInstance = new PublicClientApplication(MsalConfig)

export function MsalInitializer({ children }: { children: React.ReactNode }) {
	const [isInitialized, setIsInitialized] = useState(false)

	useEffect(() => {
		const initialize = async (): Promise<void> => {
			await msalInstance.initialize()
			try {
				await msalInstance.handleRedirectPromise()
				const account = util.GetMsalAccount(msalInstance)
				account.IfSome((acc) => {
					msalInstance.setActiveAccount(acc)
				})
			} catch (error) {
				console.error("Error during redirect handling:", error)
			} finally {
				setIsInitialized(true)
			}
		}

		initialize()
	}, [])

	return !isInitialized ? <Loading /> : <>{children}</>
}

root.render(
	<React.StrictMode>
		<ThemeProvider>
			<CssBaseline />
			<MsalProvider instance={msalInstance}>
				<MsalInitializer>
					<Provider store={Store}>
						<AppWithAuth />
					</Provider>
				</MsalInitializer>
			</MsalProvider>
		</ThemeProvider>
	</React.StrictMode>
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
ReportWebVitals()
